// Pure-SVG seating diagram. Given a group's people ordering and a seating
// config { shape, leftEnd, rightEnd, seat1Index }, renders a table with
// numbered chairs around the perimeter. Reused by the seating modal's live
// preview and by the PDF export view.
//
// Geometry: slots are indexed clockwise from the top-left corner. A person
// at index i sits at geometric slot (i + seat1Index) % n.

const CHAIR_R = 15;
const CHAIR_SPACING = 54;
const SIDE_PADDING = 28;
const LABEL_SPACE = 44;
const LABEL_FONT = 9;
const MIN_TABLE_W = 120;
const MIN_TABLE_H = 80;
const STROKE = 1.3;

function displayName(fullName) {
  if (!fullName) return "";
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  let first = parts[0];
  if (first.length > 10) first = first.slice(0, 9) + "…";
  if (parts.length === 1) return first;
  return `${first} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

function clampEnds(leftEnd, rightEnd, n) {
  const le = Math.max(0, Math.min(leftEnd ?? 0, n));
  const re = Math.max(0, Math.min(rightEnd ?? 0, n - le));
  return { leftEnd: le, rightEnd: re };
}

// Returns { slots: [{x,y}], tableShape: <svg props> } in local coordinates
// where (0,0) is top-left of the viewBox.
export function computeLayout({ shape, leftEnd, rightEnd, n }) {
  if (n <= 0) return { slots: [], viewW: 0, viewH: 0, table: null };

  if (shape === "circular") {
    // n chairs on a circle, starting at 12 o'clock, going clockwise.
    const R = Math.max(70, (n * CHAIR_SPACING) / (2 * Math.PI));
    const pad = CHAIR_R + LABEL_SPACE;
    const viewW = 2 * (R + pad);
    const viewH = viewW;
    const cx = viewW / 2;
    const cy = viewH / 2;
    const slots = [];
    for (let i = 0; i < n; i++) {
      const theta = -Math.PI / 2 + (2 * Math.PI * i) / n;
      slots.push({ x: cx + R * Math.cos(theta), y: cy + R * Math.sin(theta), side: "radial" });
    }
    const tableR = R - SIDE_PADDING;
    return {
      slots,
      viewW,
      viewH,
      table: { type: "circle", cx, cy, r: Math.max(36, tableR) },
    };
  }

  // Rectangular.
  const { leftEnd: le, rightEnd: re } = clampEnds(leftEnd, rightEnd, n);
  const rem = n - le - re;
  const topSide = Math.ceil(rem / 2);
  const bottomSide = rem - topSide;

  const spanX = Math.max(topSide, bottomSide);
  const spanY = Math.max(le, re);
  const tableW = Math.max(MIN_TABLE_W, spanX * CHAIR_SPACING);
  const tableH = Math.max(MIN_TABLE_H, spanY * CHAIR_SPACING);

  const pad = SIDE_PADDING + CHAIR_R + LABEL_SPACE;
  const viewW = tableW + 2 * pad;
  const viewH = tableH + 2 * pad;
  const tableLeft = pad;
  const tableTop = pad;
  const tableRight = tableLeft + tableW;
  const tableBottom = tableTop + tableH;

  const slots = [];
  // Top side (left -> right)
  for (let i = 0; i < topSide; i++) {
    const x = tableLeft + ((i + 0.5) * tableW) / topSide;
    slots.push({ x, y: tableTop - SIDE_PADDING, side: "top" });
  }
  // Right end (top -> bottom)
  for (let i = 0; i < re; i++) {
    const y = tableTop + ((i + 0.5) * tableH) / re;
    slots.push({ x: tableRight + SIDE_PADDING, y, side: "right" });
  }
  // Bottom side (right -> left)
  for (let i = 0; i < bottomSide; i++) {
    const x = tableRight - ((i + 0.5) * tableW) / bottomSide;
    slots.push({ x, y: tableBottom + SIDE_PADDING, side: "bottom" });
  }
  // Left end (bottom -> top)
  for (let i = 0; i < le; i++) {
    const y = tableBottom - ((i + 0.5) * tableH) / le;
    slots.push({ x: tableLeft - SIDE_PADDING, y, side: "left" });
  }

  return {
    slots,
    viewW,
    viewH,
    table: { type: "rect", x: tableLeft, y: tableTop, w: tableW, h: tableH, rx: 8 },
  };
}

export default function SeatingDiagram({
  shape = "rect",
  leftEnd = 1,
  rightEnd = 1,
  people = [],
  host = null,
  seat1Index = 0,
  onSeatClick,
  size = 320,
  showNames = false,
  peopleMap = null,
  stroke = "currentColor",
  paper = "var(--paper-0)",
  muted = "var(--ink-4)",
  ink = "var(--ink)",
  accent = "var(--accent)",
  accentSoft = "var(--accent-soft)",
}) {
  const n = people.length;
  const { slots, viewW, viewH, table } = computeLayout({ shape, leftEnd, rightEnd, n });

  if (n === 0 || !table) {
    return (
      <div style={{
        width: size, height: size, display: "flex",
        alignItems: "center", justifyContent: "center",
        color: muted, fontSize: 12, fontStyle: "italic",
      }}>
        No seats to show
      </div>
    );
  }

  const clickable = typeof onSeatClick === "function";
  const getName = (pid) => peopleMap?.get?.(pid)?.Name || "";

  // slot -> person index: person i sits at slot (i + seat1Index) % n
  // so slot s has person index (s - seat1Index + n) % n
  const personAtSlot = (s) => (s - seat1Index + n) % n;

  // Table centre, used to radiate labels outward from each chair.
  const tableCx = table.type === "circle" ? table.cx : table.x + table.w / 2;
  const tableCy = table.type === "circle" ? table.cy : table.y + table.h / 2;

  // Use a constant units-to-pixels ratio so chairs render the same size
  // across shapes; the SVG width just follows the viewBox aspect.
  const REFERENCE_W = 280;
  const scale = size / REFERENCE_W;

  return (
    <svg
      width={viewW * scale}
      height={viewH * scale}
      viewBox={`0 0 ${viewW} ${viewH}`}
      style={{ display: "block", maxWidth: "100%" }}
    >
      {/* table surface */}
      {table.type === "rect" ? (
        <rect
          x={table.x} y={table.y} width={table.w} height={table.h}
          rx={table.rx}
          fill={paper}
          stroke={stroke}
          strokeWidth={STROKE}
        />
      ) : (
        <circle
          cx={table.cx} cy={table.cy} r={table.r}
          fill={paper}
          stroke={stroke}
          strokeWidth={STROKE}
        />
      )}

      {slots.map((s, idx) => {
        const pi = personAtSlot(idx);
        const pid = people[pi];
        const seatNum = pi + 1;
        const isHost = host && pid === host;
        const isSeat1 = pi === 0;
        return (
          <g
            key={idx}
            onClick={clickable ? () => onSeatClick(idx) : undefined}
            style={{ cursor: clickable ? "pointer" : "default" }}
          >
            <circle
              cx={s.x} cy={s.y} r={CHAIR_R}
              fill={isSeat1 ? accent : isHost ? accentSoft : paper}
              stroke={isSeat1 ? accent : stroke}
              strokeWidth={isSeat1 ? STROKE * 1.3 : STROKE}
            />
            <text
              x={s.x} y={s.y}
              textAnchor="middle"
              dominantBaseline="central"
              style={{
                fontSize: `${CHAIR_R * 0.42}px`,
                fontWeight: 500,
                userSelect: "none",
                pointerEvents: "none",
              }}
              fill={isSeat1 ? paper : "var(--ink-3)"}
              fontFamily="var(--ui)"
            >
              {seatNum}
            </text>
            {showNames && pid && (() => {
              const offset = CHAIR_R + 10;
              let lx = s.x;
              let ly = s.y;
              let anchor = "middle";
              if (s.side === "top") { ly = s.y - offset; }
              else if (s.side === "bottom") { ly = s.y + offset; }
              else if (s.side === "left") { lx = s.x - offset; anchor = "end"; }
              else if (s.side === "right") { lx = s.x + offset; anchor = "start"; }
              else {
                const dx = s.x - tableCx;
                const dy = s.y - tableCy;
                const mag = Math.hypot(dx, dy) || 1;
                lx = s.x + (dx / mag) * offset;
                ly = s.y + (dy / mag) * offset;
                anchor = dx / mag < -0.3 ? "end" : dx / mag > 0.3 ? "start" : "middle";
              }
              return (
                <text
                  x={lx}
                  y={ly}
                  textAnchor={anchor}
                  dominantBaseline="central"
                  fontSize={LABEL_FONT}
                  fill={ink}
                  fontFamily="var(--ui)"
                  style={{ userSelect: "none", pointerEvents: "none" }}
                >
                  {displayName(getName(pid))}
                </text>
              );
            })()}
          </g>
        );
      })}
    </svg>
  );
}
